/**
 * The scorer's outbound queue.
 *
 * EVERY visit goes through here, online or not. There is no separate live
 * path: pressing Enter enqueues, and the drain in scorerRoom.js is the only
 * thing that ever sends. Connected, an item clears in about fifty
 * milliseconds and the marker sees no difference.
 *
 * That is the point. V1 had a live path and a replay path, they drifted, and
 * the replay path — the one nobody exercises until the wifi dies mid-
 * tournament — was the one carrying the bugs (V2-CARRYOVER.md §11). Here the
 * offline path IS the normal path, used a few hundred times a night, so it
 * cannot rot unnoticed.
 *
 * WHAT THIS HOLDS: events still to send. Never game state. The board's actual
 * state comes from the room on every sync and is projected over this — see
 * legMath.projectMatch. A tablet that reloads reads a queue of pending
 * submissions, not a picture of the match, which is what makes a reconnect a
 * reconnect instead of a restart.
 *
 * The id is minted HERE, at the moment the marker presses the button, before
 * any network attempt. That ordering is the whole idempotency story: a write
 * that reaches the room but whose acknowledgement is lost gets retried under
 * the same id and the room recognises it. V1 queued on catch with no id at
 * all, which is why a dropped response became a duplicate visit.
 */

const KEY = (tournamentId, boardNumber) =>
  `hd_scorer_queue_${tournamentId}_${boardNumber}`;

/** Storage is shared state; React needs telling when it moves. */
const listeners = new Set();

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function announce() {
  for (const fn of listeners) fn();
}

export function readQueue(tournamentId, boardNumber) {
  try {
    const raw = localStorage.getItem(KEY(tournamentId, boardNumber));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // Unreadable storage is an empty queue, not a crash. Losing queued visits
    // is bad; refusing to score at all is worse, and the room still holds
    // everything already acknowledged.
    return [];
  }
}

function writeQueue(tournamentId, boardNumber, queue) {
  try {
    localStorage.setItem(KEY(tournamentId, boardNumber), JSON.stringify(queue));
  } catch {
    // Quota or private browsing. The in-memory queue still drains for as long
    // as the page lives; Scorer.jsx surfaces the risk to the marker.
  }
  announce();
}

/**
 * Adds an event and returns its id.
 *
 * `attempts` is carried from the start so a stuck item is visible in storage
 * without having to infer it from timing.
 */
export function enqueue(tournamentId, boardNumber, type, payload) {
  const item = {
    clientId:  crypto.randomUUID(),
    type,
    payload,
    queuedAt:  new Date().toISOString(),
    attempts:  0,
  };

  const queue = readQueue(tournamentId, boardNumber);
  queue.push(item);
  writeQueue(tournamentId, boardNumber, queue);

  return item.clientId;
}

/** Drops an acknowledged item. Safe to call for an id that is already gone. */
export function acknowledge(tournamentId, boardNumber, clientId) {
  const queue = readQueue(tournamentId, boardNumber);
  const next  = queue.filter((item) => item.clientId !== clientId);

  if (next.length !== queue.length) writeQueue(tournamentId, boardNumber, next);
}

/** Records a failed attempt so a jammed item can be told from a slow one. */
export function recordAttempt(tournamentId, boardNumber, clientId) {
  const queue = readQueue(tournamentId, boardNumber);
  const item  = queue.find((entry) => entry.clientId === clientId);

  if (!item) return;

  item.attempts = (item.attempts ?? 0) + 1;
  writeQueue(tournamentId, boardNumber, queue);
}
